import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Package, FlaskConical } from "lucide-react";
import { api } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";
import styles from "./Categories.module.css";
import RawMaterialDetailModal from "../../components/Materials/RawMaterialDetailModal";
import FormulationDetailModal from "../../components/Formulations/FormulationDetailModal";

export default function CategoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const catName = location.state?.name || t("categoryDetail.fallbackTitle");

  const TABS = [
    {
      key: "materials",
      label: t("categoryDetail.rawMaterials"),
      icon: Package,
    },
    {
      key: "recipes",
      label: t("categoryDetail.topFormulas"),
      icon: FlaskConical,
    },
  ];

  const [activeTab, setActiveTab] = useState("materials");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedFormulation, setSelectedFormulation] = useState(null);

  const fetchItems = useCallback(
    async (tab, pg) => {
      if (pg === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await api.get(`/category-items/${id}`, {
          params: { type: tab, page: pg },
        });
        if (res.data.code === 200) {
          const data = res.data.data?.items || [];
          setItems((prev) => (pg === 1 ? data : [...prev, ...data]));
          setLastPage(res.data.pagination?.last_page || 1);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [id],
  );

  useEffect(() => {
    setItems([]);
    setPage(1);
    fetchItems(activeTab, 1);
  }, [activeTab, fetchItems]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchItems(activeTab, next);
  };

  return (
    <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.pageHeader}>
        <button
          className={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
        </button>
        <h1 className={styles.pageTitle}>{catName}</h1>
      </div>

      <div className={styles.tabsWrap}>
        <div className={styles.tabs}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.grid}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            <p>
              {t("categoryDetail.emptyState", {
                type: TABS.find((t) => t.key === activeTab)?.label,
              })}
            </p>
          </div>
        ) : (
          <>
            <div className={styles.grid}>
              {items.map((item) =>
                activeTab === "materials" ? (
                  <MaterialCard
                    key={item.id}
                    item={item}
                    onClick={() => setSelectedMaterial(item)}
                    t={t}
                  />
                ) : (
                  <FormulaCard
                    key={item.id}
                    item={item}
                    onViewDetails={() => setSelectedFormulation(item)}
                    t={t}
                  />
                ),
              )}
            </div>

            {page < lastPage && (
              <div className={styles.loadMoreWrap}>
                <button
                  className={styles.loadMoreBtn}
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore
                    ? t("categoryDetail.loading")
                    : t("categoryDetail.loadMore")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedMaterial && (
        <RawMaterialDetailModal
          material={selectedMaterial}
          onClose={() => setSelectedMaterial(null)}
        />
      )}
      {selectedFormulation && (
        <FormulationDetailModal
          formulation={selectedFormulation}
          onClose={() => setSelectedFormulation(null)}
        />
      )}
    </div>
  );
}

function MaterialCard({ item, onClick, t }) {
  return (
    <div className={styles.card} onClick={onClick}>
      <div className={styles.cardImage}>
        {item.image ? (
          <img src={item.image} alt={item.name} />
        ) : (
          <PlaceholderIcon />
        )}
      </div>
      <div className={styles.cardBody}>
        <h3 className={styles.cardName}>{item.name}</h3>
        {item.family && (
          <span className={styles.familyChip}>{item.family.name}</span>
        )}
        {item.description && (
          <p className={styles.cardDesc}>{item.description}</p>
        )}
        {item.cas_number && (
          <p className={styles.casNumber}>
            <span className={styles.casLabel}>
              {t("categoryDetail.casNumber")}{" "}
            </span>
            <strong>{item.cas_number}</strong>
          </p>
        )}
      </div>
    </div>
  );
}

function FormulaCard({ item, onViewDetails, t }) {
  const categoryName = item.categories?.[0]?.name || item.type?.name || "";
  return (
    <div className={styles.card}>
      <div className={styles.cardImage}>
        {item.image ? (
          <img src={item.image} alt={item.name} />
        ) : (
          <PlaceholderIcon />
        )}
      </div>
      <div className={styles.cardBody}>
        <h3 className={styles.cardName}>{item.name}</h3>
        {categoryName && (
          <span className={styles.familyChip}>{categoryName}</span>
        )}
        {item.description && (
          <p className={styles.cardDesc}>{item.description}</p>
        )}
        <button className={styles.viewBtn} onClick={onViewDetails}>
          {t("categoryDetail.viewDetails")}
        </button>
      </div>
    </div>
  );
}

function PlaceholderIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="rgba(12,104,57,0.3)"
      strokeWidth="1.2"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
